// InsightList — bulleted list variant of an Insight tile.
// Pulls ``label_key`` and ``value_key`` from display config; renders one
// row per data row. Used for low-cardinality insights where a table
// would be visually noisy.
import React from 'react';
import * as Icons from 'lucide-react';
import { formatInsightValue } from './format';

export function InsightList({ insight, loading, error, isAr }) {
  const { title, description, display } = insight || {};
  const rows = insight?.rows || [];
  const Icon = (display?.icon && Icons[display.icon]) || Icons.Sparkles;
  const labelKey = display?.label_key || 'group';
  const valueKey = display?.value_key;

  return (
    <div
      data-testid={`insight-list-${insight?.key || 'unknown'}`}
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="space-y-1">
          <div className="text-sm font-semibold text-slate-700">{title}</div>
          {description && (
            <div className="text-xs text-slate-400 leading-relaxed">{description}</div>
          )}
        </div>
        {Icon && (
          <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 text-indigo-600">
            <Icon size={18} />
          </div>
        )}
      </div>

      {error ? (
        <div className="text-sm text-rose-600">{isAr ? 'تعذّر تحميل البيانات' : 'Unable to load data'}</div>
      ) : loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 rounded bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 text-center py-4">
          {isAr ? 'لا توجد بيانات للعرض' : 'No data available'}
        </div>
      ) : (
        <ul className="text-sm text-slate-700 space-y-2">
          {rows.map((row, idx) => (
            <li
              key={idx}
              className="flex items-center justify-between border-b border-slate-50 last:border-0 pb-2 last:pb-0"
            >
              <span className="truncate">{formatInsightValue(row[labelKey])}</span>
              {valueKey && (
                <span className="font-semibold text-slate-700">
                  {formatInsightValue(row[valueKey], display?.value_format)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default InsightList;
